import React, { useState, useEffect, useRef } from 'react';
import ClassNames from 'src/components/componentHelper/ClassNames';
import { textColors } from 'src/constants/wall.constant';
import { socialCardKeys } from 'src/constants/socialcard.constant';

const CommonAudioCard = ({ cardWidth, cardItem, fontStyle, darkModeEnabled, cardFontColor, darkCardTheme, linkColor, engagementDisabled, smallSize, refresh, cardBackgroundObject={}, flatEdges = false }) => {
  /* Flat edges for the Graphic card, which has square corners everywhere else - the video
     path already zeroes its radii (GraphicVideoCard + .graphic-main-video-encloser), and
     audio has to agree with it. Every other widget type keeps the rounding, so this is a
     prop rather than a change to the shared styles. */
  const mediaRadius = flatEdges ? '0' : '10px';

  const [isWaiting, setIsWaiting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSource, setAudioSource] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const animationRef = useRef();

  // Initialize Audio Context and Analyzer
  useEffect(() => {
    try {
      const context = new (window.AudioContext || window.webkitAudioContext)();
      const analyserNode = context.createAnalyser();
      analyserNode.fftSize = 256; // Reduced for better bar visualization
      setAudioContext(context);
      setAnalyser(analyserNode);

      return () => {
        if (context && context.state !== 'closed') {
          context.close();
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    } catch (err) {
      console.log('Error initializing audio context:', err);
    }
  }, []);

  // Set audio source when cardItem changes
  useEffect(() => {
    if (cardItem && cardItem[socialCardKeys.audioAssetUrl]) {
      const audioUrl = cardItem[socialCardKeys.audioAssetUrl];
      // const proxyUrl = `https://helper.famewall.io/audio-proxy?url=${encodeURIComponent(audioUrl.trim())}`;
      const proxyUrl = audioUrl;
      setAudioSource(proxyUrl);
    }
  }, [cardItem]);


// Setup audio visualizer when audio source and refs are ready
// Inside your useEffect for the audio visualizer
// Inside your useEffect for the audio visualizer
useEffect(() => {
  if (!audioContext || !analyser || !audioRef.current || !canvasRef.current || !audioSource) return;

  const audio = audioRef.current;
  const canvas = canvasRef.current;
  const ctx = canvas.getContext('2d');
  
  let source;

  const connectAudioSource = () => {
    try {
      // Only create a new media element source if one doesn't exist
      if (!source) {
        source = audioContext.createMediaElementSource(audio);
        source.connect(analyser);
        analyser.connect(audioContext.destination);
      }
    } catch (err) {
      console.log('Error connecting audio source:', err);
    }
  };

  // Set a higher FFT size for more detailed frequency data
  analyser.fftSize = 2048;
  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  // Function to draw initial state (horizontal line)
  const drawInitialState = () => {
    const WIDTH = canvas.width;
    const HEIGHT = canvas.height;
    
    // Clear the canvas with dark background
    ctx.fillStyle = 'rgb(13, 17, 23)';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
    
    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, WIDTH, 0);
    gradient.addColorStop(0, 'rgba(0, 162, 255, 0.8)'); // Blue at left
    gradient.addColorStop(1, 'rgba(0, 255, 255, 0.8)'); // Cyan at right
    
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 2;
    
    // Draw a gentle sine wave when idle
    ctx.beginPath();
    const amplitude = HEIGHT * 0.05; // 5% of height
    const wavelength = WIDTH / 3;    // 3 waves across width
    
    ctx.moveTo(0, HEIGHT / 2);
    
    for (let x = 0; x <= WIDTH; x += 5) {
      const y = HEIGHT / 2 + amplitude * Math.sin((x / wavelength) * Math.PI * 2);
      ctx.lineTo(x, y);
    }
    
    ctx.stroke();
    
    // Add symmetric wave on bottom
    ctx.beginPath();
    ctx.moveTo(0, HEIGHT / 2);
    
    for (let x = 0; x <= WIDTH; x += 5) {
      const y = HEIGHT / 2 - amplitude * Math.sin((x / wavelength) * Math.PI * 2);
      ctx.lineTo(x, y);
    }
    
    // Add some glow effect
    ctx.shadowBlur = 15;
    ctx.shadowColor = 'rgba(0, 200, 255, 0.5)';
    ctx.stroke();
  };

  // Draw initial state right away
  drawInitialState();

  const drawWaveform = () => {
    animationRef.current = requestAnimationFrame(drawWaveform);
    
    const WIDTH = canvas.width;
    const HEIGHT = canvas.height;
  
    // Get time domain data for waveform
    const timeDataArray = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(timeDataArray);
    
    // Also get frequency data for color intensity
    const freqDataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(freqDataArray);
    
    // Calculate average frequency for color intensity
    const avgFrequency = freqDataArray.reduce((a, b) => a + b, 0) / freqDataArray.length;
    const intensity = avgFrequency / 255;
    
    // Clear the canvas
    ctx.fillStyle = 'rgb(13, 17, 23)';
    ctx.fillRect(0, 0, WIDTH, HEIGHT);
  
    // Create dynamic gradient based on audio intensity
    const gradient = ctx.createLinearGradient(0, 0, WIDTH, 0);
    gradient.addColorStop(0, `rgba(0, ${162 + (intensity * 93)}, 255, 1)`);  // More dynamic blue
    gradient.addColorStop(1, `rgba(0, 255, ${255 - (intensity * 100)}, 1)`); // Cyan to blue shift
  
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.beginPath();
  
    // Calculate how many points to skip for smooth rendering
    const sliceWidth = WIDTH / (timeDataArray.length / 2);
    let x = 0;
  
    // Start from left
    ctx.moveTo(0, HEIGHT / 2);
  
    // Draw the waveform
    for (let i = 0; i < timeDataArray.length; i += 2) {
      // Normalize value from 0-255 to 0-1, then scale to canvas height
      const v = (timeDataArray[i] / 128.0);
      const y = v * HEIGHT / 2;
      
      // Add slight randomization for more natural wave movement
      const jitter = Math.random() * 2 - 1;
      
      if (i === 0) {
        ctx.moveTo(x, y + jitter);
      } else {
        // Use quadratic curves for smoother lines
        const prevX = x - sliceWidth;
        const prevY = (timeDataArray[i-2] / 128.0) * HEIGHT / 2;
        const cpX = prevX + (x - prevX) / 2;
        
        ctx.quadraticCurveTo(cpX, prevY, x, y + jitter);
      }
      
      x += sliceWidth;
    }
  
    // Draw a mirror of the waveform on the bottom half for symmetry
    for (let i = timeDataArray.length - 1; i >= 0; i -= 2) {
      const v = (timeDataArray[i] / 128.0);
      const y = HEIGHT - (v * HEIGHT / 2);
      
      const jitter = Math.random() * 2 - 1;
      x -= sliceWidth;
      ctx.lineTo(x, y + jitter);
    }
  
    // Complete the path and fill
    ctx.lineTo(0, HEIGHT / 2);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.globalAlpha = 0.8;
    ctx.fill();
    
    // Add an outer stroke for definition
    ctx.globalAlpha = 1;
    ctx.stroke();
    
    // Add glow effect
    ctx.shadowBlur = 10 + (intensity * 15);
    ctx.shadowColor = `rgba(0, ${Math.floor(100 + intensity * 155)}, 255, 0.8)`;
  };

  try {
    // Resume audio context if it's suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    connectAudioSource();
    
    // Set up event listeners for play and pause
    audio.addEventListener('play', () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      drawWaveform();
    });
    
    audio.addEventListener('pause', () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        drawInitialState();
      }
    });
    
    audio.addEventListener('ended', () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        drawInitialState();
      }
    });
    
    // If audio is not playing initially, show the horizontal line
    if (audio.paused) {
      drawInitialState();
    }
    
  } catch (err) {
    
  }

  return () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    // Remove event listeners
    audio.removeEventListener('play', () => {});
    audio.removeEventListener('pause', () => {});
    audio.removeEventListener('ended', () => {});
  };
}, [audioSource, audioContext, analyser]);

  // Toggle play/pause
  const togglePlay = () => {
    if (!audioRef.current || !audioSource) return;

    // Resume audio context if needed (required by browsers)
    if (audioContext && audioContext.state === 'suspended') {
      audioContext.resume();
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  return (
    <div 
      className={ClassNames(
        darkModeEnabled ? "border-none " : "border-none bg-white ",
        "border-none relative z-0 transition-all w-full h-full card-holder"
      )} 
      style={{ maxWidth: "39rem", cursor: 'default', minWidth: cardWidth, borderRadius: mediaRadius, ...(cardBackgroundObject ? cardBackgroundObject : {}) }}
    >
      <div 
        className={ClassNames(
          "relative w-full h-full",
          (cardItem[socialCardKeys.videoSummary] && cardItem[socialCardKeys.videoSummary] !== '') ? '' : 'overflow-hidden'
        )} 
        style={{ 
          color: !darkModeEnabled ? (cardFontColor || textColors.black.primary) : (cardFontColor || textColors.light.primary), 
          fontSize: !smallSize ? "1.15em" : "1.0em", 
          lineHeight: !smallSize ? 1.5 : 1.7,
          borderRadius: mediaRadius,
          backgroundColor: '#000'
        }}
      >
        {/* Hidden audio element - we'll control it programmatically */}
        <audio
          crossOrigin="anonymous"
          ref={audioRef}
          src={audioSource}
          preload="metadata"
          onPlay={() => {
            setIsPlaying(true);
            setIsWaiting(false);
          }}
          onPause={() => {
            setIsPlaying(false);
            setIsWaiting(false);
          }}
          onEnded={() => setIsPlaying(false)}
          onError={(e) => console.log('Audio playback error', e)}
          style={{ display: 'none' }}
        />

        {/* Audio Visualizer Canvas */}
        <div className={ClassNames("relative w-full h-full cursor-pointer  overflow-hidden shadow-xl", flatEdges ? "" : "rounded-xl")} onClick={() => togglePlay()}>
          <canvas
            ref={canvasRef}
            className="w-full h-full"
            width={1920}
            height={1080}
            style={{ borderRadius: mediaRadius, backgroundColor: '#000000' }}
          />
        </div>

        {/* Audio Controls Overlay - Only show when not playing */}
        {!isPlaying && (
          <div className="absolute bottom-0 transition duration-300 ease-in-out py-2 px-4 w-full opacity-100 bg-gradient-to-t from-black to-transparent">
            <div className="flex w-full justify-between items-center">
              <svg
                onClick={() => togglePlay()}
                className="h-10 w-10 text-white cursor-pointer"
                width={60} height={60} viewBox="0 0 32 32" fill="none" aria-hidden="true" slot="play"
              >
                <path
                  d="M10.6667 6.6548C10.6667 6.10764 11.2894 5.79346 11.7295 6.11862L24.377 15.4634C24.7377 15.7298 24.7377 16.2692 24.3771 16.5357L11.7295 25.8813C11.2895 26.2065 10.6667 25.8923 10.6667 25.3451L10.6667 6.6548Z"
                  fill="currentColor"
                />
              </svg>

              <div className={ClassNames("flex flex-col text-white items-end", fontStyle && fontStyle !== '' ? `font-${fontStyle}` : '')}>
                <p className="text-lg font-semibold" style={{ textAlign: 'end' }}>
                  {cardItem[socialCardKeys.authorName]}
                </p>
                <a
                  href={(cardItem[socialCardKeys.messageLink] && cardItem[socialCardKeys.messageLink] !== '') ? cardItem[socialCardKeys.messageLink] : void(0)}
                  target={(cardItem[socialCardKeys.messageLink] && cardItem[socialCardKeys.messageLink] !== '') ? "_blank" : "_self"}
                  rel="noreferrer noopener"
                  className={ClassNames(
                    "text-base", 
                    (cardItem[socialCardKeys.messageLink] && cardItem[socialCardKeys.messageLink] !== '') ? 'hover:underline customs' : 'no-underline'
                  )}
                  style={{ textAlign: 'end', color: 'inherit' }}
                >
                  {cardItem[socialCardKeys.workTitle]}
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Loading spinner */}
        {isWaiting && (
          <svg
            version={1.0}
            width="45px"
            height="45px"
            viewBox="0 0 128 128"
            xmlSpace="preserve"
            className='absolute inset-0 m-auto'
          >
            <g>
              <path
                d="M70.1 9.42l-10.7 9.4v-5.77a50.82 50.82 0 0 0-43.7 68.23L11 79.7l-1.5 5.53A58 58 0 0 1 59.4 5.8V0z"
                fill="#F3F4F6"
              />
              <path
                d="M70.1 9.42l-10.7 9.4v-5.77a50.82 50.82 0 0 0-43.7 68.23L11 79.7l-1.5 5.53A58 58 0 0 1 59.4 5.8V0z"
                fill="#F3F4F6"
                transform="rotate(120 64 64)"
              />
              <path
                d="M70.1 9.42l-10.7 9.4v-5.77a50.82 50.82 0 0 0-43.7 68.23L11 79.7l-1.5 5.53A58 58 0 0 1 59.4 5.8V0z"
                fill="#F3F4F6"
                transform="rotate(240 64 64)"
              />
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 64 64"
                to="120 64 64"
                dur="240ms"
                repeatCount="indefinite"
              />
            </g>
          </svg>
        )}
      </div>

      {/* Audio Summary/Description */}
      {cardItem && cardItem[socialCardKeys.videoSummary] && cardItem[socialCardKeys.videoSummary] !== '' && (
        <div 
          className="px-5 py-4 rounded-md shadow-md text-left" 
          style={!darkModeEnabled 
            ? { color: (cardFontColor || textColors.black.primary), fontSize: !smallSize ? "1.15em" : "1.0em", lineHeight: !smallSize ? 1.5 : 1.7, textDecoration: 'none' } 
            : { color: (cardFontColor || textColors.light.primary), fontSize: !smallSize ? "1.15em" : "1.0em", lineHeight: !smallSize ? 1.5 : 1.7, textDecoration: 'none' }
          }
        >
          <q 
            className={ClassNames("italic", fontStyle && fontStyle !== '' ? `font-${fontStyle}` : '')} 
            style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {cardItem[socialCardKeys.videoSummary]}
          </q>
        </div>
      )}
    </div>
  );
};

export default CommonAudioCard;
